import type { FlowDefinition, FlowStep, SessionView, StepKind } from '@fitting-room/contracts'

/**
 * Step order is the flow document's order, and advancing is the client's job:
 * the session state machine has a `goto` command and deliberately does not
 * move the cursor by itself. Every step therefore ends by batching its own
 * command together with a `goto`, so a step's effect and the move to the next
 * one either both land or neither does.
 */
export function stepIndex(flow: FlowDefinition, stepId: string | undefined): number {
  if (!stepId) return 0
  const index = flow.steps.findIndex((s) => s.id === stepId)
  return index >= 0 ? index : 0
}

export function currentStep(view: SessionView): FlowStep {
  const index = stepIndex(view.flow, view.session.currentStepId)
  const step = view.flow.steps[index] ?? view.flow.steps[0]
  if (!step) throw new Error('flow has no steps')
  return step
}

export function nextStepId(flow: FlowDefinition, stepId: string | undefined): string | undefined {
  return flow.steps[stepIndex(flow, stepId) + 1]?.id
}

export function previousStepId(flow: FlowDefinition, stepId: string | undefined): string | undefined {
  const index = stepIndex(flow, stepId)
  return index > 0 ? flow.steps[index - 1]?.id : undefined
}

export function firstStepIdOfKind(flow: FlowDefinition, kind: StepKind): string | undefined {
  return flow.steps.find((s) => s.kind === kind)?.id
}

/**
 * The screen to draw — the cursor, with one override, and it is the last one.
 *
 * A session she has SENT opens on the screen that tells her so, wherever the
 * cursor points. The cursor is where she stopped, which after a submit is a
 * fact about history rather than about her: nothing is being asked any more,
 * the domain refuses every command that would change what the boutique pulls,
 * and question seven with a Continue that cannot save is a worse answer than
 * the one screen with something on it.
 *
 * Today the cursor is almost always the summary already, because that is where
 * the send button is. This is not for that case. It is for the ones where it is
 * not: a session a stylist submitted on her behalf from wherever she abandoned
 * it, a seed that writes the status directly, a flow that grows a step after
 * the summary. Depending on the cursor being right is depending on a
 * coincidence that nothing enforces.
 *
 * Sent is not EXPIRED. An expired token never resolves to a view at all, and
 * merging the two would tell a customer whose link ran out that her list is
 * safely with the boutique.
 */
export function visibleStep(view: SessionView): FlowStep {
  if (view.session.status !== 'submitted') return currentStep(view)
  const id = firstStepIdOfKind(view.flow, 'summary') ?? firstStepIdOfKind(view.flow, 'fitting_room')
  // No summary in the flow: nowhere else to send her, so the cursor stands.
  return view.flow.steps.find((s) => s.id === id) ?? currentStep(view)
}

/**
 * Where she is in the QUESTIONNAIRE, which is not the same as where she is in
 * the flow.
 *
 * The questionnaire is everything between the welcome and the deck: eleven
 * screens for Velvet Hour today. Its length belongs to that boutique's flow
 * document and not to this file — adding or dropping a question moves the
 * counter and nothing else has to be told, which is how the face-shape screen
 * left without a line of this file changing. Once she reaches the deck the asking is over,
 * so the progress reads complete: browsing, shortlisting and the summary are
 * the reward, not more of the form.
 *
 * `ref/design/before-the-hour/Quiz.dc.html` draws this as one filled segment
 * per screen rather than a percentage: a bar that creeps says "still going",
 * and a row of segments says "four left".
 */
export interface FlowProgress {
  /** 1-based position among the questionnaire steps, or `total` once past it. */
  position: number
  total: number
  /** Screens still to answer. Zero once she is browsing. */
  remaining: number
  /** False on the welcome, the deck, and everything after it. */
  inQuestionnaire: boolean
}

/**
 * The first step that is no longer asking: the deck, a review screen — or the
 * one that decides which rail to open on.
 *
 * `line_choice` belongs here rather than among the questions because of what it
 * asks about. Every other screen before the deck asks about HER — her date, her
 * measurements, the shapes she likes — and the counter is a promise about how
 * many of those are left. "Where shall we start?" is a question about the shop,
 * and counting it made that promise wrong in both directions: the total read
 * "of 13" while the step drew no counter of its own, so she walked 1/13 to
 * 12/13 and then hit a screen with no counter at all. Thirteen of thirteen
 * never appeared.
 */
const AFTER_THE_ASKING: ReadonlySet<string> = new Set(['browse', 'shortlist', 'summary', 'fitting_room', 'booking'])

/**
 * Screens that ask about the SHOP rather than about her, and so are not
 * counted — but do not end the asking either.
 *
 * `line_choice` used to end it, when it was the last screen before the deck.
 * In the fitting room it is the second screen, with her size and her
 * silhouettes still to come; ending the count there would have told her she
 * was finished two questions early.
 */
const NOT_COUNTED: ReadonlySet<string> = new Set(['welcome', 'line_choice'])

function questionnaireEnd(flow: FlowDefinition): number {
  const index = flow.steps.findIndex((s) => AFTER_THE_ASKING.has(s.kind))
  return index >= 0 ? index : flow.steps.length
}

function questionnaireStepIds(flow: FlowDefinition): string[] {
  return flow.steps
    .slice(0, questionnaireEnd(flow))
    .filter((s) => !NOT_COUNTED.has(s.kind))
    .map((s) => s.id)
}

export function flowProgress(view: SessionView): FlowProgress {
  const ids = questionnaireStepIds(view.flow)
  const total = ids.length
  const current = view.session.currentStepId
  const index = current ? ids.indexOf(current) : -1
  if (index < 0) {
    // Not one of the counted screens. Where she is, is how many of them lie
    // behind her: past the end of the asking that is all of them, and on an
    // uncounted screen in the middle of it — the one that picks a catalogue —
    // it is however many she has already walked.
    const at = stepIndex(view.flow, current)
    const end = questionnaireEnd(view.flow)
    if (at >= end) return { position: total, total, remaining: 0, inQuestionnaire: false }
    const behind = view.flow.steps.slice(0, at).filter((s) => ids.includes(s.id)).length
    return { position: behind, total, remaining: total - behind, inQuestionnaire: false }
  }
  return { position: index + 1, total, remaining: total - index - 1, inQuestionnaire: true }
}

/**
 * Where "edit what you told us" begins.
 *
 * The first screen that ASKS her something, which is not the same as the first
 * step of the flow — the welcome asks nothing — and not the same as the one
 * before wherever she is now. A control called "Edit profile" that lands on
 * "Anything else we should know?" is a lie, and hard-coding a step id here
 * would make it one for the next flow that reorders its questions.
 */
export function firstQuestionnaireStepId(flow: FlowDefinition): string | undefined {
  return questionnaireStepIds(flow)[0]
}
